import { useEffect, useMemo, useState, type FormEvent, type JSX } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Trash2, Users } from 'lucide-react';
import { Button, Card, CardContent, Input } from '@caratuva/ui';
import { Incoterm, UpdateInvoiceInput } from '@caratuva/shared-types';
import { ApiError, getInvoice, listInvoices, updateInvoice } from '../lib/api';
import {
  BuyerPicker,
  Field,
  FieldError,
  Section,
  SUPPORTED_CURRENCIES,
  selectClass,
  type KnownBuyer,
} from '../components/invoice-form';

const INCOTERMS = Incoterm.options;

interface LineItemDraft {
  description: string;
  quantity: string;
  unitPrice: string;
}

function emptyLineItem(): LineItemDraft {
  return { description: '', quantity: '1', unitPrice: '0.00' };
}

function formatDecimal2(value: number): string {
  if (!Number.isFinite(value)) return '0.00';
  return value.toFixed(2);
}

function computeSubtotal(quantity: string, unitPrice: string): string {
  const q = Number.parseFloat(quantity);
  const p = Number.parseFloat(unitPrice);
  if (!Number.isFinite(q) || !Number.isFinite(p)) return '0.00';
  return formatDecimal2(q * p);
}

type FieldErrors = Partial<Record<string, string>>;

function InvoiceEdit(): JSX.Element {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['invoice', id],
    queryFn: ({ signal }) => getInvoice(id, signal),
    staleTime: 60_000,
  });
  const invoice = query.data;

  const knownBuyersQuery = useQuery({
    queryKey: ['invoices'],
    queryFn: ({ signal }) => listInvoices(signal),
    staleTime: 60_000,
  });
  const knownBuyers = useMemo<KnownBuyer[]>(() => {
    const byEmail = new Map<string, KnownBuyer>();
    for (const inv of knownBuyersQuery.data ?? []) {
      const email = inv.buyerEmail;
      if (!email) continue;
      const existing = byEmail.get(email);
      const lastUsedAt = inv.createdAt;
      if (!existing || lastUsedAt > existing.lastUsedAt) {
        byEmail.set(email, { email, name: inv.buyerName ?? null, lastUsedAt });
      }
    }
    return Array.from(byEmail.values()).sort((a, b) =>
      a.lastUsedAt < b.lastUsedAt ? 1 : a.lastUsedAt > b.lastUsedAt ? -1 : 0,
    );
  }, [knownBuyersQuery.data]);

  const [pickerOpen, setPickerOpen] = useState(false);
  function selectBuyer(b: KnownBuyer): void {
    setBuyerEmail(b.email);
    setBuyerName(b.name ?? '');
    setPickerOpen(false);
  }

  const [buyerEmail, setBuyerEmail] = useState('');
  const [buyerName, setBuyerName] = useState('');
  const [currency, setCurrency] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [buyerRef, setBuyerRef] = useState('');
  const [dueNumber, setDueNumber] = useState('');
  const [exportContractRef, setExportContractRef] = useState('');
  const [incoterm, setIncoterm] = useState('');
  const [portOfLoading, setPortOfLoading] = useState('');
  const [lineItems, setLineItems] = useState<LineItemDraft[]>([emptyLineItem()]);
  const [prefilled, setPrefilled] = useState(false);

  useEffect(() => {
    if (!invoice || prefilled) return;
    setBuyerEmail(invoice.buyerEmail ?? '');
    setBuyerName(invoice.buyerName ?? '');
    setCurrency(invoice.currency);
    setDueDate(invoice.dueDate ? invoice.dueDate.slice(0, 10) : '');
    setBuyerRef(invoice.buyerRef ?? '');
    setDueNumber(invoice.dueNumber ?? '');
    setExportContractRef(invoice.exportContractRef ?? '');
    setIncoterm(invoice.incoterm ?? '');
    setPortOfLoading(invoice.portOfLoading ?? '');
    if (invoice.lineItems && invoice.lineItems.length > 0) {
      setLineItems(
        invoice.lineItems.map((li) => ({
          description: li.description,
          quantity: li.quantity,
          unitPrice: li.unitPrice,
        })),
      );
    }
    setPrefilled(true);
  }, [invoice, prefilled]);

  const subtotals = useMemo(
    () => lineItems.map((li) => computeSubtotal(li.quantity, li.unitPrice)),
    [lineItems],
  );

  const total = useMemo(() => {
    const sum = subtotals.reduce((acc, s) => acc + Number.parseFloat(s || '0'), 0);
    return formatDecimal2(sum);
  }, [subtotals]);

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (input: Parameters<typeof updateInvoice>[1]) => updateInvoice(id, input),
    onSuccess: (data) => {
      queryClient.setQueryData(['invoice', id], data);
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      void navigate({ to: '/invoices/$id', params: { id } });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 400) {
        const body = err.body as { message?: unknown } | null;
        const messages = Array.isArray(body?.message)
          ? (body.message as string[])
          : typeof body?.message === 'string'
            ? [body.message]
            : [];
        const next: FieldErrors = {};
        for (const m of messages) {
          const match = /^([a-zA-Z0-9_.[\]]+)[:\s-]+\s*(.+)$/.exec(m);
          if (match && match[1] && match[2]) next[match[1]] = match[2];
        }
        setFieldErrors(next);
        setFormError(
          messages.length > 0 ? messages.join('; ') : 'Please fix the highlighted fields.',
        );
        return;
      }
      setFormError(err instanceof Error ? err.message : 'Failed to update invoice.');
    },
  });

  function addLineItem(): void {
    setLineItems((items) => [...items, emptyLineItem()]);
  }

  function removeLineItem(index: number): void {
    setLineItems((items) => (items.length <= 1 ? items : items.filter((_, i) => i !== index)));
  }

  function updateLineItem(index: number, patch: Partial<LineItemDraft>): void {
    setLineItems((items) => items.map((it, i) => (i === index ? { ...it, ...patch } : it)));
  }

  function lineItemError(
    index: number,
    field: keyof LineItemDraft | 'subtotal',
  ): string | undefined {
    return fieldErrors[`lineItems.${index}.${field}`];
  }

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setFieldErrors({});
    setFormError(null);

    const candidate: Parameters<typeof updateInvoice>[1] = {
      buyerEmail: buyerEmail.trim() || undefined,
      buyerName: buyerName.trim() || undefined,
      currency: currency || undefined,
      lineItems: lineItems.map((li, i) => ({
        description: li.description.trim(),
        quantity: li.quantity.trim(),
        unitPrice: li.unitPrice.trim(),
        subtotal: subtotals[i] ?? '0.00',
      })),
    };
    if (dueDate) candidate.dueDate = new Date(dueDate).toISOString();
    else candidate.dueDate = null;
    if (buyerRef.trim()) candidate.buyerRef = buyerRef.trim();
    else candidate.buyerRef = null;
    if (dueNumber.trim()) candidate.dueNumber = dueNumber.trim();
    else candidate.dueNumber = null;
    if (exportContractRef.trim()) candidate.exportContractRef = exportContractRef.trim();
    else candidate.exportContractRef = null;
    if (incoterm) candidate.incoterm = incoterm as typeof candidate.incoterm;
    else candidate.incoterm = null;
    if (portOfLoading.trim()) candidate.portOfLoading = portOfLoading.trim();
    else candidate.portOfLoading = null;

    const parsed = UpdateInvoiceInput.safeParse(candidate);
    if (!parsed.success) {
      const next: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const path = issue.path.join('.');
        if (!next[path]) next[path] = issue.message;
      }
      setFieldErrors(next);
      setFormError('Please fix the highlighted fields.');
      return;
    }

    mutation.mutate(parsed.data);
  }

  if (query.isPending) {
    return (
      <section className="mx-auto max-w-5xl">
        <p className="text-sm text-navy-600">Loading invoice...</p>
      </section>
    );
  }

  if (query.isError || !invoice) {
    return (
      <section className="mx-auto max-w-5xl">
        <p className="text-sm text-[var(--color-danger)]">Failed to load invoice.</p>
      </section>
    );
  }

  if (invoice.status !== 'draft' && invoice.status !== 'awaiting_approval') {
    return (
      <section className="mx-auto max-w-5xl space-y-4">
        <Link
          to="/invoices/$id"
          params={{ id }}
          className="inline-flex items-center gap-1 text-sm text-navy-700 transition-colors hover:text-navy-900"
        >
          <ArrowLeft aria-hidden className="h-4 w-4" strokeWidth={1.5} />
          Back to invoice
        </Link>
        <p className="text-sm text-[var(--color-danger)]">
          Invoice can only be edited in draft or awaiting_approval status.
        </p>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-5xl">
      <Link
        to="/invoices/$id"
        params={{ id }}
        className="inline-flex items-center gap-1 text-sm text-navy-700 transition-colors hover:text-navy-900"
      >
        <ArrowLeft aria-hidden className="h-4 w-4" strokeWidth={1.5} />
        Back to invoice
      </Link>

      <div className="mt-3 mb-10 border-b border-navy-100 pb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-navy-900">Edit invoice</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Update this invoice before it is approved.
        </p>
      </div>

      <form onSubmit={onSubmit} className="space-y-10" noValidate>
        <Section title="Buyer" description="Buyer email and name shown on the invoice.">
          <Card className="border-navy-100 shadow-xs">
            <CardContent className="space-y-5 p-6">
              {knownBuyers.length > 0 ? (
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setPickerOpen(true)}
                  >
                    <Users aria-hidden className="h-4 w-4" strokeWidth={1.5} />
                    Choose existing buyer
                  </Button>
                </div>
              ) : null}
              <div className="grid gap-5 sm:grid-cols-2">
                <Field id="buyerEmail" label="Buyer email" error={fieldErrors.buyerEmail}>
                  <Input
                    id="buyerEmail"
                    type="email"
                    autoComplete="email"
                    value={buyerEmail}
                    onChange={(e) => setBuyerEmail(e.target.value)}
                  />
                </Field>
                <Field id="buyerName" label="Buyer name" error={fieldErrors.buyerName}>
                  <Input
                    id="buyerName"
                    value={buyerName}
                    onChange={(e) => setBuyerName(e.target.value)}
                  />
                </Field>
              </div>
            </CardContent>
          </Card>
          <BuyerPicker
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            buyers={knownBuyers}
            onSelect={selectBuyer}
          />
        </Section>

        <Section
          title="Line items"
          description="Subtotal is computed as quantity multiplied by unit price."
        >
          <Card className="overflow-hidden border-navy-100 shadow-xs">
            <CardContent className="p-0">
              <div className="hidden grid-cols-12 gap-3 border-b border-navy-100 px-6 py-3 text-[11px] font-medium uppercase tracking-wider text-navy-700 sm:grid">
                <div className="col-span-5">Description</div>
                <div className="col-span-2">Quantity</div>
                <div className="col-span-2">Unit price</div>
                <div className="col-span-2 text-right">Subtotal</div>
                <div className="col-span-1" />
              </div>
              <div className="divide-y divide-navy-100">
                {lineItems.map((item, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-1 items-start gap-3 px-6 py-4 transition-colors hover:bg-beige-50/40 sm:grid-cols-12"
                  >
                    <div className="sm:col-span-5">
                      <Input
                        aria-label={`Description for line ${index + 1}`}
                        placeholder="Item or service"
                        value={item.description}
                        onChange={(e) => updateLineItem(index, { description: e.target.value })}
                      />
                      {lineItemError(index, 'description') ? (
                        <FieldError>{lineItemError(index, 'description')}</FieldError>
                      ) : null}
                    </div>
                    <div className="sm:col-span-2">
                      <Input
                        aria-label={`Quantity for line ${index + 1}`}
                        inputMode="decimal"
                        className="text-right tabular-nums"
                        value={item.quantity}
                        onChange={(e) => updateLineItem(index, { quantity: e.target.value })}
                      />
                      {lineItemError(index, 'quantity') ? (
                        <FieldError>{lineItemError(index, 'quantity')}</FieldError>
                      ) : null}
                    </div>
                    <div className="sm:col-span-2">
                      <Input
                        aria-label={`Unit price for line ${index + 1}`}
                        inputMode="decimal"
                        className="text-right tabular-nums"
                        value={item.unitPrice}
                        onChange={(e) => updateLineItem(index, { unitPrice: e.target.value })}
                      />
                      {lineItemError(index, 'unitPrice') ? (
                        <FieldError>{lineItemError(index, 'unitPrice')}</FieldError>
                      ) : null}
                    </div>
                    <div className="self-center text-right text-sm font-medium tabular-nums text-navy-900 sm:col-span-2">
                      {subtotals[index] ?? '0.00'}
                    </div>
                    <div className="flex justify-end self-center sm:col-span-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeLineItem(index)}
                        disabled={lineItems.length <= 1}
                        aria-label={`Remove line ${index + 1}`}
                        className="text-navy-600 hover:bg-danger/10 hover:text-danger"
                      >
                        <Trash2 aria-hidden className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between border-t border-navy-100 bg-beige-50/60 px-6 py-4">
                <Button type="button" variant="outline" size="sm" onClick={addLineItem}>
                  <Plus aria-hidden className="h-4 w-4" strokeWidth={1.5} />
                  Add line item
                </Button>
                <div className="flex items-baseline gap-3">
                  <span className="text-xs font-medium uppercase tracking-wider text-navy-700">
                    Total
                  </span>
                  <span className="font-mono text-lg font-semibold tabular-nums text-navy-900">
                    {total}
                  </span>
                  <span className="text-sm font-medium text-navy-700">{invoice.currency}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </Section>

        <Section
          title="Invoice details"
          description="Due date and optional export trade references."
        >
          <Card className="border-navy-100 shadow-xs">
            <CardContent className="grid gap-5 p-6 sm:grid-cols-2">
              <Field id="currency" label="Currency" error={fieldErrors.currency}>
                <select
                  id="currency"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className={selectClass}
                >
                  {SUPPORTED_CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </Field>
              <Field id="dueDate" label="Due date" error={fieldErrors.dueDate}>
                <Input
                  id="dueDate"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
              </Field>
              <Field id="buyerRef" label="Buyer reference" error={fieldErrors.buyerRef}>
                <Input
                  id="buyerRef"
                  value={buyerRef}
                  onChange={(e) => setBuyerRef(e.target.value)}
                />
              </Field>
              <Field id="dueNumber" label="DU-E number" error={fieldErrors.dueNumber}>
                <Input
                  id="dueNumber"
                  value={dueNumber}
                  onChange={(e) => setDueNumber(e.target.value)}
                  placeholder="11 digits"
                />
              </Field>
              <Field
                id="exportContractRef"
                label="Export contract ref"
                error={fieldErrors.exportContractRef}
              >
                <Input
                  id="exportContractRef"
                  value={exportContractRef}
                  onChange={(e) => setExportContractRef(e.target.value)}
                />
              </Field>
              <Field id="incoterm" label="Incoterm" error={fieldErrors.incoterm}>
                <select
                  id="incoterm"
                  value={incoterm}
                  onChange={(e) => setIncoterm(e.target.value)}
                  className={selectClass}
                >
                  <option value="">None</option>
                  {INCOTERMS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </Field>
              <Field id="portOfLoading" label="Port of loading" error={fieldErrors.portOfLoading}>
                <Input
                  id="portOfLoading"
                  value={portOfLoading}
                  onChange={(e) => setPortOfLoading(e.target.value)}
                />
              </Field>
            </CardContent>
          </Card>
        </Section>

        {formError ? (
          <div
            role="alert"
            className="rounded-md border border-[var(--color-danger)] bg-[var(--color-danger)]/10 p-3 text-sm text-[var(--color-danger)]"
          >
            {formError}
          </div>
        ) : null}

        <div className="flex items-center justify-end gap-3 border-t border-navy-100 pt-6">
          <Button asChild variant="ghost">
            <Link to="/invoices/$id" params={{ id }}>
              Cancel
            </Link>
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving...' : 'Save changes'}
          </Button>
        </div>
      </form>
    </section>
  );
}

export const Route = createFileRoute('/_app/invoices/$id/edit')({
  component: InvoiceEdit,
});
